import { ApiError } from "@/lib/api-client"

/** Health of the AI provider the pipeline uses (GET /api/admin/ai/health). */
export type AiHealthStatus = "ok" | "unknown" | "credits" | "auth" | "no_key" | "rate_limit" | "unreachable"

export interface AiHealth {
  provider: string
  providerName: string
  /** Set when `provider` (the configured one) is down and the pipeline runs on this provider meanwhile. */
  fallbackProvider: string | null
  fallbackProviderName: string | null
  status: AiHealthStatus
  /** error: nothing works until someone acts. warning: AI still works or it clears on its own. */
  severity: "ok" | "warning" | "error"
  /** The provider's own error text. */
  detail: string
  source: "call" | "probe" | "keys" | ""
  since: string | null
  checkedAt: string | null
  /** A re-check is running on the server. */
  checking: boolean
  keys: { active: number; parked: number; disabled: number; env: boolean; envParked: boolean }
  recheckSeconds: number
}

export const AI_HEALTH_KEY = ["ai-health"] as const

/** Where to top up each provider. */
export const BILLING_URLS: Record<string, string> = {
  claude: "https://console.anthropic.com/settings/billing",
  deepseek: "https://platform.deepseek.com/top_up",
  gemini: "https://aistudio.google.com/",
}

export const API_KEYS_SETTINGS = "/dashboard/settings?tab=keys"

/** The request never reached the API server (offline, server down, DNS, CORS). */
export function isNetworkError(error: unknown) {
  if (error instanceof ApiError) return error.status === 0
  return error instanceof TypeError && /fetch|network|load failed/i.test(error.message)
}

const AI_OR_CONNECTION_TEXT =
  /credit|balance|billing|payment|api key|rate.?limit|quota|overloaded|unreachable|connect|timed? ?out|network/i

/**
 * Whether a failed request may mean the AI provider or the connection is down: then the health status is
 * re-checked at once instead of waiting for the next poll.
 */
export function pointsToAiOrConnectionProblem(error: unknown) {
  if (isNetworkError(error)) return true
  if (error instanceof ApiError && error.status >= 500) return true
  return error instanceof Error && AI_OR_CONNECTION_TEXT.test(error.message)
}
